/**
 * @file meetingExportPick.ts
 * @brief B50: what the Export / Send sheet has ticked, and the `pick` body it
 *        sends (pure, no React).
 *
 * @details
 * The server (GET /api/boardroom/sessions/:id/export) lists the sections the
 * caller can see, each with its items, plus the presets. The sheet keeps one
 * list of ticked item ids per section; a section is "on" when all its items
 * are ticked, "partly" (3/7) when some are. Transcript has no items: it is on
 * or off with a from/to range in seconds.
 *
 * The body follows the contract (docs/product/meeting-export-b50.md):
 *   - the state still equals a preset → `{ preset }` alone, so "Full minutes"
 *     reuses today's cached minutes PDF;
 *   - anything else → `{ sections }`, `true` for a whole section,
 *     `{ items }` for part of one, `{ fromSecs, toSecs }` for part of the
 *     transcript.
 *
 * @date 02-11-2026 (B50-W)
 */

export type ExportItem = {
  id: string;
  text?: string;
  label?: string;
  owner?: string | null;
  due?: string | null;
  done?: boolean;
  mine?: boolean;
  guest?: boolean;
  state?: string | null;
  bullets?: { text: string; children?: string[] }[];
  openQuestions?: string[];
  topic?: string | null;
};

export type ExportSection = {
  key: string;
  label: string;
  count: number;
  available: boolean;
  defaultOn: boolean;
  items: ExportItem[];
  durationSecs?: number;
};

export type ExportPreset = {
  key: string;
  label: string;
  sections: string[];
  available: boolean;
  count?: number;
};

export type ExportLastPick = {
  preset?: string | null;
  sections?: string[];
  savedAt?: string;
} | null;

export type ExportCatalogue = {
  meeting: { id: string; title: string; startedAt?: string | null; tier?: string; isOwner?: boolean };
  sections: ExportSection[];
  presets: ExportPreset[];
  lastPick: ExportLastPick;
  messageMax: number;
};

export type PickState = {
  /** Ticked item ids per item section (absent / [] = off). */
  items: Record<string, string[]>;
  /** Transcript on, and its range in whole seconds. */
  transcript: { on: boolean; fromSecs: number; toSecs: number };
};

export type PickBody =
  | { preset: string }
  | { sections: Record<string, true | { items: string[] } | { fromSecs: number; toSecs: number }> };

export const TRANSCRIPT_KEY = "transcript";

function sectionOf(cat: ExportCatalogue, key: string): ExportSection | undefined {
  return cat.sections.find((s) => s.key === key);
}

function transcriptDuration(cat: ExportCatalogue): number {
  return Math.max(0, Math.ceil(Number(sectionOf(cat, TRANSCRIPT_KEY)?.durationSecs ?? 0)));
}

/** Nothing ticked; the transcript range spans the whole meeting. */
export function emptyPick(cat: ExportCatalogue): PickState {
  return { items: {}, transcript: { on: false, fromSecs: 0, toSecs: transcriptDuration(cat) } };
}

/** The ticks a preset stands for (My to-dos = the caller's own to-dos). */
export function applyPreset(cat: ExportCatalogue, presetKey: string): PickState {
  const pick = emptyPick(cat);
  const preset = cat.presets.find((p) => p.key === presetKey);
  if (!preset) return pick;
  for (const key of preset.sections) {
    const section = sectionOf(cat, key);
    if (!section?.available) continue;
    if (key === TRANSCRIPT_KEY) {
      pick.transcript.on = true;
      continue;
    }
    const items = presetKey === "my_todos" ? section.items.filter((i) => i.mine) : section.items;
    pick.items[key] = items.map((i) => i.id);
  }
  return pick;
}

/** Every item of the named sections that this meeting has (last pick). */
export function pickFromSections(cat: ExportCatalogue, keys: string[]): PickState {
  const pick = emptyPick(cat);
  for (const key of keys) {
    const section = sectionOf(cat, key);
    if (!section?.available) continue;
    if (key === TRANSCRIPT_KEY) pick.transcript.on = true;
    else pick.items[key] = section.items.map((i) => i.id);
  }
  return pick;
}

/**
 * Where the sheet opens: the last pick's preset if this meeting offers it,
 * else the last pick's sections (if any of them is here), else Full minutes.
 */
export function initialPick(cat: ExportCatalogue): PickState {
  const last = cat.lastPick;
  if (last?.preset && cat.presets.some((p) => p.key === last.preset && p.available)) {
    return applyPreset(cat, last.preset);
  }
  if (last?.sections?.length) {
    const pick = pickFromSections(cat, last.sections);
    if (!isEmptyPick(pick)) return pick;
  }
  return applyPreset(cat, "full");
}

export function isEmptyPick(pick: PickState): boolean {
  return !pick.transcript.on && Object.values(pick.items).every((ids) => ids.length === 0);
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((x) => s.has(x));
}

function samePick(cat: ExportCatalogue, a: PickState, b: PickState): boolean {
  if (a.transcript.on !== b.transcript.on) return false;
  if (a.transcript.on) {
    const full = transcriptDuration(cat);
    const range = (p: PickState) =>
      isWholeTranscript(p, full) ? "all" : `${p.transcript.fromSecs}-${p.transcript.toSecs}`;
    if (range(a) !== range(b)) return false;
  }
  const keys = new Set([...Object.keys(a.items), ...Object.keys(b.items)]);
  for (const k of keys) if (!sameSet(a.items[k] ?? [], b.items[k] ?? [])) return false;
  return true;
}

function isWholeTranscript(pick: PickState, durationSecs: number): boolean {
  return pick.transcript.fromSecs <= 0 && pick.transcript.toSecs >= durationSecs;
}

/** The available preset the ticks still equal, if any (highlighted on top). */
export function matchingPreset(cat: ExportCatalogue, pick: PickState): string | null {
  for (const p of cat.presets) {
    if (!p.available) continue;
    if (samePick(cat, pick, applyPreset(cat, p.key))) return p.key;
  }
  return null;
}

/** The `pick` the routes take. Null when nothing is ticked. */
export function pickBody(cat: ExportCatalogue, pick: PickState): PickBody | null {
  if (isEmptyPick(pick)) return null;
  const preset = matchingPreset(cat, pick);
  if (preset) return { preset };
  const sections: Record<string, true | { items: string[] } | { fromSecs: number; toSecs: number }> = {};
  for (const section of cat.sections) {
    if (!section.available) continue;
    if (section.key === TRANSCRIPT_KEY) {
      if (!pick.transcript.on) continue;
      const full = transcriptDuration(cat);
      sections[section.key] = isWholeTranscript(pick, full)
        ? true
        : { fromSecs: pick.transcript.fromSecs, toSecs: pick.transcript.toSecs };
      continue;
    }
    const ticked = pick.items[section.key] ?? [];
    if (!ticked.length) continue;
    // Server order, so the PDF lists items as the meeting does.
    const ids = section.items.map((i) => i.id).filter((id) => ticked.includes(id));
    sections[section.key] = ids.length === section.items.length ? true : { items: ids };
  }
  return { sections };
}

export type SectionTick = "on" | "partly" | "off";

export function sectionTick(cat: ExportCatalogue, pick: PickState, key: string): SectionTick {
  if (key === TRANSCRIPT_KEY) return pick.transcript.on ? "on" : "off";
  const section = sectionOf(cat, key);
  const n = (pick.items[key] ?? []).length;
  if (!section || n === 0) return "off";
  return n >= section.items.length ? "on" : "partly";
}

/**
 * "3/7" beside a section header, "0/7" when none is ticked and "7/7" when all
 * are (U2, phone parity); the transcript: null when off, "All" or its range.
 */
export function sectionCount(cat: ExportCatalogue, pick: PickState, key: string): string | null {
  const section = sectionOf(cat, key);
  if (!section) return null;
  if (key === TRANSCRIPT_KEY) {
    if (!pick.transcript.on) return null;
    const full = transcriptDuration(cat);
    return isWholeTranscript(pick, full)
      ? "All"
      : `${clock(pick.transcript.fromSecs)}–${clock(pick.transcript.toSecs)}`;
  }
  const n = (pick.items[key] ?? []).length;
  return `${n}/${section.items.length}`;
}

/** Section box clicked: off or partly → all items; on → none. */
export function toggleSection(cat: ExportCatalogue, pick: PickState, key: string): PickState {
  if (key === TRANSCRIPT_KEY) {
    return { ...pick, transcript: { ...pick.transcript, on: !pick.transcript.on } };
  }
  const section = sectionOf(cat, key);
  if (!section) return pick;
  const on = sectionTick(cat, pick, key) === "on";
  return { ...pick, items: { ...pick.items, [key]: on ? [] : section.items.map((i) => i.id) } };
}

export function toggleItem(pick: PickState, key: string, id: string): PickState {
  const now = pick.items[key] ?? [];
  const next = now.includes(id) ? now.filter((x) => x !== id) : [...now, id];
  return { ...pick, items: { ...pick.items, [key]: next } };
}

/** Clamp a from/to range to 0 … duration, from ≤ to. */
export function setTranscriptRange(
  cat: ExportCatalogue,
  pick: PickState,
  fromSecs: number,
  toSecs: number,
): PickState {
  const full = transcriptDuration(cat);
  const clamp = (n: number) => Math.min(full, Math.max(0, Math.round(Number.isFinite(n) ? n : 0)));
  let from = clamp(fromSecs);
  let to = clamp(toSecs);
  if (from > to) [from, to] = [to, from];
  return { ...pick, transcript: { on: true, fromSecs: from, toSecs: to } };
}

/** 754 → "12:34"; an hour or more → "1:02:34". */
export function clock(secs: number): string {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${String(m).padStart(2, "0")}:${ss}`;
}

/** "12:34" / "1:02:34" → seconds, a bare "12" = 12 minutes; null when it does not read. */
export function parseClock(text: string): number | null {
  const t = text.trim();
  if (!t) return null;
  if (/^\d+$/.test(t)) return Number(t) * 60;
  const parts = t.split(":").map((p) => p.trim());
  if (parts.some((p) => !/^\d+$/.test(p)) || parts.length > 3) return null;
  return parts.reduce((acc, p) => acc * 60 + Number(p), 0);
}

/** The quick path: one to-do (its id) or one decision (its index) as text. */
export function singleItemPick(kind: "todos" | "decisions", id: string): PickBody {
  return { sections: { [kind]: { items: [id] } } };
}
